import React from 'react';
import { Box, Stack, Typography, Button, Paper } from '@mui/material';
import { Clock, Unlock, AlertTriangle } from 'lucide-react';
import { convertSecondsToTimeTaken } from '../../../../../../utils/helpers/time';
import { UserAssessment } from '../../../../../../utils/types/assessment_types';

interface SelectedAssessmentModalProps {
  selectedAssessment: UserAssessment | undefined;
  setModalContent: (content: number) => void;
  setIsPractice: (value: boolean) => void;
  setAfterHelpModalContent: (content: number) => void;
}

export const SelectedAssessmentModal: React.FC<SelectedAssessmentModalProps> = ({
  selectedAssessment,
  setModalContent,
  setIsPractice,
  setAfterHelpModalContent,
}) => {
  const HELP_SEEN_KEY = 'hiacuity_help_seen';

  const handleStartFlow = (isPracticeMode: boolean) => {
    setIsPractice(isPracticeMode);
    const alreadySeen = localStorage.getItem(HELP_SEEN_KEY);
    if (!alreadySeen) {
      // First time — show help screens, then go to privacy (5)
      setAfterHelpModalContent(5);
      setModalContent(4);
    } else {
      // Already seen — go directly to privacy notice (5)
      setModalContent(5);
    }
  };

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      {selectedAssessment?.assessment.description && (
        <Typography variant='body2' color='text.secondary'>
          {selectedAssessment.assessment.description}
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Paper
          variant='outlined'
          sx={{
            p: 2,
            flex: 1,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'primary.50',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Unlock size={18} />
          </Box>
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Total Score
            </Typography>
            <Typography variant='subtitle2' fontWeight={600}>
              {selectedAssessment?.assessment.totalScore ?? 0} points
            </Typography>
          </Box>
        </Paper>

        <Paper
          variant='outlined'
          sx={{
            p: 2,
            flex: 1,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: 'info.50',
              color: 'info.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Clock size={18} />
          </Box>
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Duration
            </Typography>
            <Typography variant='subtitle2' fontWeight={600}>
              {selectedAssessment?.assessment.totalTime
                ? `${convertSecondsToTimeTaken(selectedAssessment.assessment.totalTime)} mins`
                : 'Untimed'}
            </Typography>
          </Box>
        </Paper>
      </Stack>

      <Typography variant='body2' color='text.secondary'>
        Disclaimer: During the process of the assessment your browser actions will be monitored.
      </Typography>

      <Paper
        variant='outlined'
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'error.50',
          borderColor: 'error.light',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <AlertTriangle size={20} color='#d32f2f' />
        <Typography variant='body2' color='error.dark' fontWeight={500}>
          Warning: Once you start the assessment, you must complete it and you cannot re-take it again.
        </Typography>
      </Paper>

      <Stack
        direction='row'
        spacing={2}
        justifyContent='flex-end'
        sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button
          onClick={() => handleStartFlow(true)}
          variant='outlined'
          color='inherit'
        >
          Practice Tutorial
        </Button>
        <Button
          onClick={() => handleStartFlow(false)}
          variant='contained'
          color='primary'
        >
          Start Assessment
        </Button>
      </Stack>
    </Stack>
  );
};
